import React, { useState, useCallback, useEffect } from "react";

import {
    Box, 
    TextField,
    Grid,
    Switch,
    FormControlLabel,
    styled,
    Divider,
    IconButton,
    InputAdornment,
    Button
} from '@mui/material';

import {
  LoadingButton
} from '@mui/lab';

import { useFormik } from 'formik';
import { useRegions, regionTypes, targetTypes } from '../../../hooks/api/useRegions';
import { useAppContextFeatures } from '../../../context/AppContext';
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { initialValues, initialRowValues, validationSchema }from "../../../schema/region/areaSchema"
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from "../../../hooks/useTranslations";

import { CommonIcons, icons } from "../../common/CommonIcons";
import CommonRestricted from '../../common/CommonRestricted';

const StyledCard = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export default function UpdateArea({
    routeType,
    id,
    parentId,
    handleOnSuccess
}) {
    const { t } = useTranslations({ ns: ["common", "@region"] })

    // const { id } = useParams();
    
    const { promptMessage } = useSystemPrompt();
    const { isLoading, update, get } = useRegions({ regionType: regionTypes.area, targetType: targetTypes.area });
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.region.id, scopeTypes.region.area.id)

    // const navigate = useNavigate();
    const { setSystemLoading } = useAppContextFeatures();

    // const { getRoute } = useSystemRoutes(routeTypes.admin);
    const [ savedValues, setSavedValues ] = useState()

    const onSubmit = async (currentValues) => {

        setSystemLoading(true)

        try{

            let postcode = "";
            let postcodeArray = [];
            
            currentValues.rows.forEach((e, index) => {
                postcodeArray.push(e?.postcode)
            })

            postcode = postcodeArray.join(",");

            let data = {
                area: {
                    area_name: currentValues?.areaName,
                    postcode_string: postcode,
                    status: currentValues?.status? 1 : 0,
                }
            }
    
            const response = await update({ id, data, targetType: targetTypes.area});
    
            promptMessage(true, `${response?.message}`)

            if(handleOnSuccess) handleOnSuccess()
            
        }catch(error){
            promptMessage(false, `Action Failed (${error?.message})`)
        }

        setSystemLoading(false)
    }

    const formik = useFormik({
        initialValues: savedValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;  

    const loadData = async (id) => {

        setSystemLoading(true)

        try{

            const { data } = await get({ id })
            const { area } = data;

            const {
                area_name: _areaName,
                postcode_string: _postcode,
                status: _status
            } = area

            const _postcodeArray = _postcode && _postcode.indexOf(",")? _postcode.split(',') : [initialRowValues]
            let rows = [];

            console.log('_pos', _postcodeArray)
            _postcodeArray.forEach((e) => {
                rows.push({
                    postcode: typeof e === 'object'? e?.postcode: e
                })
            })

            setSavedValues({
                areaName: _areaName,
                status: _status === '1',
                rows
            })

        }catch(error){
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

    }

    useEffect(() => {

        loadData(id);

    }, [])

    const RenderForm = () => {

        if(!values) return <></>

        return(
            
            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={0.5}>

                        <Grid item xs={12} sm={12} md={12}>
                            <Grid container spacing={0.5}>

                                <Grid item xs={12} sm={12} md={8}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        autoFocus
                                        fullWidth
                                        name={"areaName"}
                                        label={t("area")}
                                        type="text"                                         
                                        variant="outlined"
                                        value={values?.areaName}
                                        onChange={handleChange}
                                        error={touched.areaName && Boolean(errors.areaName)}
                                        helperText={touched.areaName && errors.areaName}
                                    />
                                </Grid>

                                <Grid item xs={6} sm={6} md={4}> 
                                    <FormControlLabel 
                                        sx={{
                                            marginLeft:'1px'
                                        }}
                                        control={
                                            <Switch 
                                                name="status" 
                                                checked={Boolean(values.status)}
                                                onChange={(event, checked) => {
                                                    setFieldValue("status", checked);
                                                }}
                                            />
                                        }     
                                        label={
                                            Boolean(values.status)? t("enabled") : t("disabled")
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                </Grid>

                                <Grid container spacing={1}>
                                    <Grid item xs={12}>
                                        <Grid container spacing={1}>
                                        {
                                            values.rows.map((i, index) => {

                                                const itemValues = (values?.rows && values.rows[index])? values.rows[index] : {}
                                                const itemTouched = (touched?.rows && touched.rows[index])? touched.rows[index] : {}
                                                const itemErrors = (errors?.rows && errors.rows[index])? errors.rows[index] : {}

                                                return (
                                                    <Grid key={index} item md={2} sm={3} xs={6}>
                                                        {/* {JSON.stringify(itemErrors)} */}
                                                        <TextField
                                                            margin="dense"
                                                            size="small"
                                                            autoFocus
                                                            fullWidth
                                                            name={"postcode"}
                                                            label={t("postcode")}
                                                            type="text"                                         
                                                            variant="outlined"
                                                            value={itemValues?.postcode}
                                                            onChange={(event) => {
                                                                setFieldValue(`rows.${index}.postcode`, event.target.value)
                                                            }}
                                                            error={itemTouched.postcode && Boolean(itemErrors.postcode)}
                                                            helperText={itemTouched.postcode && itemErrors.postcode}
                                                            InputProps={{
                                                                sx: {
                                                                    pr: 0 
                                                                },
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton 
                                                                            color={"error"}
                                                                            onClick={() => {

                                                                                // must remain at least one item
                                                                                if(values.rows.length <= 1){

                                                                                    promptMessage(false, t(`atLeastOneAreaIsRequired`))

                                                                                }else{
                                                                                    const newRows = values.rows.filter((i2, index2) => index2 !== index)
    
                                                                                    setFieldValue(`rows`, newRows)
                                                                                }

                                                                            }}
                                                                        >
                                                                            <CommonIcons name={icons.cancel} />
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    </Grid>
                                                )
                                            })
                                        }
                                            <Grid item md={2} sm={3} xs={6}>
                                                <Button
                                                    sx={{
                                                        mt: 1,
                                                        display: "flex",
                                                        flexDirection: "row",
                                                        justifyContent: "center",
                                                        alignItems: "center"
                                                    }}
                                                    variant={"outlined"}
                                                    fullWidth
                                                    onClick={() => {
                                                        setFieldValue(
                                                            `rows`,
                                                            [...values.rows, initialRowValues]
                                                        )
                                                    }}
                                                    size="medium"
                                                >
                                                    <CommonIcons name={icons.add} />
                                                    <Box sx={{ ml: 1 }}>
                                                        {t('add')}
                                                    </Box>
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                {/* <Grid item xs={6} sm={6} md={2}>
                                    <TextField
                                        margin="normal"
                                        size="small"
                                        fullWidth
                                        name={"postcode"}
                                        label="Postcode"
                                        type="text"                                         
                                        variant="outlined"
                                        value={values?.postcode}
                                        onChange={handleChange}
                                        error={touched.postcode && Boolean(errors.postcode)}
                                        helperText={touched.postcode && errors.postcode}
                                    />
                                </Grid> */}
                            
                            </Grid>
                        </Grid>
                    </Grid>
                </StyledCard>

                <Box 
                    sx={{ 
                        mx: 2,
                        mr: 2
                    }}
                >
                    <LoadingButton
                        type="submit"
                        fullWidth
                        variant="contained"
                        loading={isLoading}
                    >
                        {t('saveChanges')}
                    </LoadingButton>
                </Box>
            </form>
        ) 
    }

    return (

        <Box>
                
            { 
                isPermissionAllow({ functionType: functionTypes.pageUpdate.id })?
                    RenderForm() 
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}